import { takeEvery, put, all, select, takeLatest, delay } from 'redux-saga/effects';
import { listActions, listReducer } from '@nextiva/engagement-ui';
import { DEFAULT_DEBOUNCE_TIME } from 'sharedParts';

import { CRM_SURVEYS_SHOW_CONNECT_SURVEYS } from 'constants/featureFlags';
import * as actions from 'constants/surveyList/SurveyListActions';
import {
  loadSurveyList,
  loadSurveyListPreviewOptions,
  getSurveyListPreviewOptions,
  setSurveyListPreviewOptions,
  addSurveyListPreviewOptions,
  setSurveyListPreviewOptionsCount,
  setSurveyListPreviewText,
} from 'actions/surveyListActions';
import { setIsSurveyNameFocused } from 'actions/surveyEditorActions';
import { navigateToSurveyEditor } from 'actions/templateListActions';
import { getFeatureFlagValue } from 'reducers/appReducer';

const { setItemListFilterSearch } = listActions;
const { getListItemSearchText } = listReducer;

function* onCreateSurveySuccess({ data: { id }, shouldReplaceLocation }) {
  yield put(navigateToSurveyEditor(id, shouldReplaceLocation));
  yield put(setIsSurveyNameFocused(true));
}

function* onLoadSurveyListPreviewOptions({ payload: { listId, text, offset, limit } }) {
  const oldSearchPreviewText = yield select(getListItemSearchText, listId);
  // if we don't have offset it's not loadMore trigger,
  // so same text may trigger update and load same items again @amudrenok
  const reset = oldSearchPreviewText !== text || !offset;
  const showConnectSurveys = yield select(getFeatureFlagValue, CRM_SURVEYS_SHOW_CONNECT_SURVEYS);

  yield put(setSurveyListPreviewText(listId, text));

  if (text !== '') {
    yield delay(DEFAULT_DEBOUNCE_TIME); /* debounce network request */
    yield put(getSurveyListPreviewOptions(listId, reset, text, offset, limit, showConnectSurveys));
  } else {
    yield put(setSurveyListPreviewOptionsCount(listId, 0));
    yield put(setSurveyListPreviewOptions(listId, []));
  }
}

function* onGetSurveyListPreviewOptionsSuccess({
  listId,
  reset,
  data: { names },
  params: [text, offset, limit], // eslint-disable-line
}) {
  yield put(
    reset
      ? setSurveyListPreviewOptions(listId, names)
      : addSurveyListPreviewOptions(listId, names, offset, limit),
  );
}

function* onLoadMoreSurveyListPreviewOptions({ payload: { listId, startIndex, stopIndex } }) {
  const searchPreviewText = yield select(getListItemSearchText, listId);
  const includedStop = stopIndex + 1;
  const limit = includedStop - startIndex;

  if (limit > 0) {
    yield put(loadSurveyListPreviewOptions(listId, searchPreviewText, startIndex, limit));
  }
}

function* onSurveySearch({ payload: { listId, text } }) {
  yield put(setItemListFilterSearch(listId, text));
  yield put(loadSurveyList(listId, { pageNumber: 0 }));
}

export default function*() {
  yield all([
    takeEvery([actions.POST_CREATE_SURVEY_SUCCESS, actions.POST_CLONE_SURVEY_SUCCESS], onCreateSurveySuccess),
    takeLatest(actions.LOAD_SURVEY_LIST_PREVIEW_OPTIONS, onLoadSurveyListPreviewOptions),
    takeEvery(actions.LOAD_MORE_SURVEY_LIST_PREVIEW_OPTIONS, onLoadMoreSurveyListPreviewOptions),
    takeEvery(actions.GET_SURVEY_LIST_PREVIEW_OPTIONS_SUCCESS, onGetSurveyListPreviewOptionsSuccess),
    takeEvery(actions.ON_SURVEY_SEARCH, onSurveySearch),
  ]);
}
